import React from 'react';
import { css } from 'emotion';
import { useQueryClient } from 'react-query';
import { connect } from 'react-redux';
import reduxForm, { InjectedFormProps } from 'redux-form/lib/reduxForm';
import Field from 'redux-form/lib/Field';
import FieldArray from 'redux-form/lib/FieldArray';
import SubmissionError from 'redux-form/lib/SubmissionError';

import { INTERACTIVE_POLL_KEY } from 'hooks/useInteractivityPolls';
import { AnchorAPI } from 'modules/AnchorAPI';
import {
  InteractivityPoll,
  POLL_TYPE,
} from 'modules/AnchorAPI/v3/episodes/fetchInteractivityPoll';

import { FieldInputWithRedux } from 'components/FieldInput';
import { FieldRadioToggleWithRedux } from 'components/FieldRadioToggle';
import {
  ConfirmationButtons,
  DatesContainer,
  PollTypeStyles,
  StyledDatePicker,
  ErrorMessage,
  StyledFieldLabel,
} from '../InteractivePoll/styles';
import { OptionsFieldArray } from './OptionsFieldArray';
import { getChangedValues, validate } from './utils';
import { SubmitDraftButton } from './SubmitDraftButton';
import { SubmitPublishButton } from './SubmitPublishButton';

type Props = {
  poll: InteractivityPoll;
  episodeId: string;
  onSuccess: () => void;
};

function PollCreatorFormView({
  handleSubmit,
  error,
  change,
  values,
  submitting,
  pristine,
  poll,
  episodeId,
  onSuccess,
}: Props & StateProps & InjectedFormProps<InteractivityPoll, Props>) {
  const queryClient = useQueryClient();
  const onSubmit = async (formValues: InteractivityPoll) => {
    const { opening_date: openingDate, closing_date: closingDate } = formValues;
    const payload = {
      ...formValues,
      opening_date: openingDate
        ? new Date(openingDate).getTime()
        : new Date().getTime(),
      closing_date: closingDate
        ? new Date(closingDate).getTime()
        : new Date().getTime(),
    };
    try {
      if (!poll.id) {
        await AnchorAPI.createInteractivityPoll(episodeId, payload);
      } else {
        const updates = getChangedValues(poll, payload);
        await AnchorAPI.updateInteractivityPoll(episodeId, updates);
      }
      onSuccess();
      return queryClient.invalidateQueries([INTERACTIVE_POLL_KEY, episodeId]);
    } catch (e) {
      throw new SubmissionError({ _error: e.message });
    }
  };
  return (
    <form>
      <StyledFieldLabel htmlFor="question">Ask a question</StyledFieldLabel>
      <Field name="question" id="question" component={FieldInputWithRedux} />
      <Field
        name="type"
        component={FieldRadioToggleWithRedux}
        cssProp={PollTypeStyles}
        label="Poll type"
        id="type"
        options={[
          { label: 'Single Choice', value: POLL_TYPE.SINGLE_CHOICE },
          { label: 'Multiple Choice', value: POLL_TYPE.MULTIPLE_CHOICE },
        ]}
      />
      <div
        className={css`
          margin-top: 1rem;
        `}
      >
        {
          // redux-form type mismatch; `'FieldArray' does not have any construct or call signatures`
          // @ts-ignore
          <FieldArray name="options" component={OptionsFieldArray} />
        }
        <StyledFieldLabel
          htmlFor="opening_date"
          className={css`
            display: block;
          `}
        >
          Poll publish date
        </StyledFieldLabel>
        <DatesContainer>
          <StyledDatePicker
            value={values.opening_date as string}
            label="Start"
            name="opening_date"
            setValue={(value: string | null) => change('opening_date', value)}
          />
          <StyledDatePicker
            value={values.closing_date as string}
            label="End"
            name="closing_date"
            setValue={(value: string | null) => change('closing_date', value)}
          />
        </DatesContainer>
        <ConfirmationButtons>
          <SubmitDraftButton
            isPublished={poll.published}
            isSubmitting={submitting}
            pristine={pristine}
            onClick={handleSubmit((formValues: InteractivityPoll) => {
              return onSubmit({
                ...formValues,
                published: false,
              });
            })}
          />
          <SubmitPublishButton
            isPublished={poll.published}
            isSubmitting={submitting}
            pristine={pristine}
            onClick={handleSubmit(onSubmit)}
          />
        </ConfirmationButtons>
      </div>
      {error && (
        <ErrorMessage>
          Sorry, looks like something went wrong. Try again?
        </ErrorMessage>
      )}
    </form>
  );
}

type StateProps = { values: InteractivityPoll };

function mapStateToProps(state: any): StateProps {
  return {
    values: state.form.episodePoll.values,
  };
}

export const InteractivePollForm = reduxForm<InteractivityPoll, Props>({
  form: 'episodePoll',
  validate,
})(connect(mapStateToProps)(PollCreatorFormView));
